import * as React from 'react';
import { useContext } from 'react';
import styled from '@emotion/styled';
import { OverlayLayout } from './OverlayLayout';
import { ImageModel } from '../../core/models/ImageModel';
import { useSystem } from '../../hooks/useSystem';
import { ButtonType, ButtonWidget } from '../controls/ButtonWidget';
import { Notification, NotificationType } from '../../core/stores/NotificationStore';
import { useNavigate } from 'react-router-dom';
import { RouteError } from '@foxbun/lib-errors';
import { ApiValidationCodes } from '@foxbun/lib-goferal-types';

export const LightBoxContext = React.createContext<{
  setImage: (image: ImageModel) => any;
  clearImage: () => any;
}>(null);

export interface LightBoxWidgetProps {
  image: ImageModel | null;
}

export const LightBoxWidget: React.FC<LightBoxWidgetProps> = (props) => {
  const context = useContext(LightBoxContext);
  const system = useSystem();
  const navigate = useNavigate();
  if (props.image) {
    return (
      <OverlayLayout
        onClick={() => {
          context.clearImage();
        }}
      >
        <S.Content>
          <S.Image src={props.image.url} />
          {props.image.canEdit() ? (
            <S.Actions
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <ButtonWidget
                label="Edit"
                type={ButtonType.SECONDARY}
                action={() => {
                  if (!props.image.editLink) {
                    return;
                  }
                  context.clearImage();
                  navigate(props.image.editLink, { state: 'push' });
                }}
              />
              <ButtonWidget
                label="Delete"
                type={ButtonType.SECONDARY}
                action={async () => {
                  const confirm = await system.dialog_store.showConfirmDialog({
                    title: 'Delete image?',
                    desc: 'This will remove the image from your gallery.'
                  });
                  if (!confirm) {
                    return;
                  }
                  try {
                    await props.image.delete();
                    system.notification_store.showNotification(
                      new Notification({
                        type: NotificationType.SUCCESS,
                        desc: 'Image deleted'
                      })
                    );
                    context.clearImage();
                  } catch {
                    system.notification_store.showNotification(
                      new Notification({
                        type: NotificationType.ERROR,
                        desc: 'Failed to delete image'
                      })
                    );
                  }
                }}
              />
            </S.Actions>
          ) : (
            <S.Actions
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <ButtonWidget
                label="Report"
                type={ButtonType.SECONDARY}
                action={async () => {
                  const confirm = await system.dialog_store.showConfirmDialog({
                    title: 'Report image?',
                    desc: 'This will send the image to moderation for review.'
                  });
                  if (!confirm) {
                    return;
                  }
                  try {
                    await props.image.report();
                    system.notification_store.showNotification(
                      new Notification({
                        type: NotificationType.SUCCESS,
                        desc: 'Image reported'
                      })
                    );
                    context.clearImage();
                  } catch (ex) {
                    if (ex instanceof RouteError && ex.error.code === ApiValidationCodes.REPORT_ALREADY_EXISTS) {
                      system.notification_store.showNotification(
                        new Notification({
                          type: NotificationType.ERROR,
                          desc: 'You have already reported this image'
                        })
                      );
                      return;
                    }
                    system.notification_store.showNotification(
                      new Notification({
                        type: NotificationType.ERROR,
                        desc: 'Failed to report image'
                      })
                    );
                  }
                }}
              />
            </S.Actions>
          )}
        </S.Content>
      </OverlayLayout>
    );
  }

  return null;
};
namespace S {
  export const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
    height: 100%;
  `;

  export const Image = styled.img`
    max-width: 100%;
    max-height: calc(100% - 72px);
  `;

  export const Actions = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    width: 100%;
  `;
}
